'use client'

import { Skeleton } from '@/ui/atoms/skeleton/Skeleton'
import { useRouter, useSearchParams } from 'next/navigation'
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAccount } from 'wagmi'
import { useStableLoadedData } from '../logic/useStableLoadedData'
import { useUnifiedEarnFilteredOpportunities } from '../logic/useUnifiedEarnFilteredOpportunities'
import { useSharedUnifiedEarnOpportunities } from '../logic/useUnifiedEarnOpportunities'
import type { AssetFilter, MarketFilter, SortDirection, SortField, UnifiedEarnFilter } from '../types'
import { ASSET_FILTERS, MARKET_FILTERS } from '../types'
import { UnifiedEarnFilterBar } from './UnifiedEarnFilterBar'
import { UnifiedEarnVirtualList } from './UnifiedEarnVirtualList'

function parseFilterFromSearchParams(searchParams: URLSearchParams): UnifiedEarnFilter {
  const marketsParam = searchParams.get('markets')
  const assetsParam = searchParams.get('assets')
  const assetFilterParam = searchParams.get('asset')
  const inWalletParam = searchParams.get('inWallet')
  const depositedParam = searchParams.get('deposited')
  const searchParam = searchParams.get('q')
  const sortByParam = searchParams.get('sortBy') as SortField | null
  const sortDirParam = searchParams.get('sortDir') as SortDirection | null

  const markets = marketsParam
    ? marketsParam.split(',').filter((m): m is MarketFilter => MARKET_FILTERS.includes(m as MarketFilter))
    : []

  const assets = assetsParam
    ? assetsParam.split(',').filter((a): a is AssetFilter => ASSET_FILTERS.includes(a as AssetFilter))
    : []

  const assetFilter = assetFilterParam ? assetFilterParam.split(',').filter(Boolean) : []

  return {
    markets,
    assets,
    assetFilter,
    inWalletOnly: inWalletParam === 'true',
    depositedOnly: depositedParam === 'true',
    searchQuery: searchParam || '',
    sortBy: sortByParam === 'tvl' ? 'tvl' : 'apy',
    sortDirection: sortDirParam === 'asc' ? 'asc' : 'desc',
  }
}

function buildSearchParams(filter: UnifiedEarnFilter): string {
  const params = new URLSearchParams()

  if (filter.markets.length > 0) {
    params.set('markets', filter.markets.join(','))
  }
  if (filter.assets.length > 0) {
    params.set('assets', filter.assets.join(','))
  }
  if (filter.assetFilter.length > 0) {
    params.set('asset', filter.assetFilter.join(','))
  }
  if (filter.inWalletOnly) {
    params.set('inWallet', 'true')
  }
  if (filter.depositedOnly) {
    params.set('deposited', 'true')
  }
  if (filter.searchQuery) {
    params.set('q', filter.searchQuery)
  }
  if (filter.sortBy !== 'apy') {
    params.set('sortBy', filter.sortBy)
  }
  if (filter.sortDirection !== 'desc') {
    params.set('sortDir', filter.sortDirection)
  }

  return params.toString()
}

interface UnifiedEarnTableSectionProps {
  /** Base path for URL sync (e.g. '/lend' or '/markets/assets') */
  basePath: string
}

export function UnifiedEarnTableSection({ basePath }: UnifiedEarnTableSectionProps) {
  return (
    <Suspense fallback={<ListSkeleton />}>
      <UnifiedEarnTableSectionContent basePath={basePath} />
    </Suspense>
  )
}

function UnifiedEarnTableSectionContent({ basePath }: UnifiedEarnTableSectionProps) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const { address } = useAccount()
  const { opportunities, isLoading } = useSharedUnifiedEarnOpportunities()
  const { data: displayedOpportunities, hasLoaded } = useStableLoadedData(opportunities, isLoading)

  const [filter, setFilter] = useState<UnifiedEarnFilter>(() => parseFilterFromSearchParams(searchParams))
  const lastSyncedQueryRef = useRef<string | null>(null)

  const filteredOpportunities = useUnifiedEarnFilteredOpportunities(displayedOpportunities, filter)

  const { assetOptions, assetAddressBySymbol } = useMemo(() => {
    const assets = new Set<string>()
    const assetAddressBySymbol: Record<string, string> = {}
    for (const opp of displayedOpportunities) {
      if (opp.assetSymbol) {
        assets.add(opp.assetSymbol)
        if (!assetAddressBySymbol[opp.assetSymbol] && opp.tokenInfo?.address) {
          assetAddressBySymbol[opp.assetSymbol] = opp.tokenInfo.address
        }
      }
    }
    return { assetOptions: Array.from(assets).sort(), assetAddressBySymbol }
  }, [displayedOpportunities])

  const handleFilterChange = useCallback((newFilter: UnifiedEarnFilter) => {
    setFilter(newFilter)
  }, [])

  // URL → state sync
  useEffect(() => {
    const urlFilter = parseFilterFromSearchParams(searchParams)
    const canonicalQueryString = buildSearchParams(urlFilter)
    if (lastSyncedQueryRef.current === canonicalQueryString) return
    lastSyncedQueryRef.current = canonicalQueryString
    setFilter(urlFilter)
  }, [searchParams])

  // State → URL sync
  useEffect(() => {
    const nextQueryString = buildSearchParams(filter)
    if (lastSyncedQueryRef.current === nextQueryString) return
    lastSyncedQueryRef.current = nextQueryString
    const newUrl = nextQueryString ? `${basePath}?${nextQueryString}` : basePath
    router.replace(newUrl, { scroll: false })
  }, [filter, router, basePath])

  // Reset wallet-related filters when wallet disconnects
  useEffect(() => {
    if (!address) {
      setFilter((prev) => {
        if (!prev.inWalletOnly && !prev.depositedOnly) return prev
        return { ...prev, inWalletOnly: false, depositedOnly: false }
      })
    }
  }, [address])

  const showLoadingState = isLoading && !hasLoaded

  return (
    <>
      <UnifiedEarnFilterBar
        filter={filter}
        onFilterChange={handleFilterChange}
        hasWallet={!!address}
        assetOptions={assetOptions}
        assetAddressBySymbol={assetAddressBySymbol}
      />

      {showLoadingState ? (
        <ListSkeleton />
      ) : filteredOpportunities.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-16">
          <p className="text-lg text-white/60">No opportunities found</p>
          <p className="text-sm text-white/40">Try adjusting your filters</p>
        </div>
      ) : (
        <UnifiedEarnVirtualList
          opportunities={filteredOpportunities}
        />
      )}
    </>
  )
}

function ListSkeleton() {
  return (
    <>
      {/* Desktop: List rows */}
      <div className="hidden flex-col gap-2 md:flex">
        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
          <div
            key={i}
            className="flex items-center justify-between rounded-xl border border-white/[0.04] bg-[#111114] p-4"
          >
            <div className="flex items-center gap-3">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="flex flex-col gap-1">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
            <div className="flex items-center gap-8">
              <Skeleton className="h-5 w-5 rounded-full" />
              <Skeleton className="h-4 w-14" />
              <Skeleton className="h-4 w-8" />
              <div className="flex flex-col items-end gap-1">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-3 w-12" />
              </div>
              <Skeleton className="h-8 w-8 rounded-full" />
            </div>
          </div>
        ))}
      </div>

      {/* Mobile: Card skeleton */}
      <div className="mx-auto flex w-full max-w-lg flex-col gap-3 md:hidden">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative shrink-0">
                  <Skeleton className="h-8 w-8 rounded-xl" />
                  <Skeleton className="absolute right-0 bottom-0 h-3.5 w-3.5 translate-x-1/4 translate-y-1/4 rounded-full" />
                </div>
                <div className="flex flex-col gap-1">
                  <Skeleton className="h-4 w-14" />
                  <Skeleton className="h-3 w-20" />
                </div>
              </div>
              <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
            </div>
            <div className="mt-2 flex flex-col gap-2 border-white/[0.04] border-t pt-3">
              <div className="flex items-center justify-between">
                <Skeleton className="h-3 w-8" />
                <Skeleton className="h-4 w-14" />
              </div>
              <div className="flex items-center justify-between">
                <Skeleton className="h-3 w-14" />
                <Skeleton className="h-4 w-14" />
              </div>
              <div className="flex items-center justify-between">
                <Skeleton className="h-3 w-8" />
                <Skeleton className="h-4 w-16" />
              </div>
              <div className="flex items-center justify-between">
                <Skeleton className="h-3 w-8" />
                <Skeleton className="h-4 w-10" />
              </div>
              <div className="flex items-center justify-between">
                <Skeleton className="h-3 w-14" />
                <Skeleton className="h-4 w-5 rounded-full" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  )
}
